import {Component, computed, inject, linkedSignal, signal, viewChild} from '@angular/core';
import {DestroyRef} from '@angular/core';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {NgbModal, NgbModalRef} from '@ng-bootstrap/ng-bootstrap';
import {UserService} from '../../../../../services/http/user/user.service';
import {ModalComponent} from '../../../../../shared/components/modal/modal.component';
import {ToastrService} from 'ngx-toastr';
import {StoreSummary} from '../../../../../classes/models/store/StoreSummary';
import {DataService} from '../../../../../services/store/data.service';
import {CheckboxItem} from '../../../../../shared/classes/CheckboxItem';
import {UserDetails} from '../../../../../classes/models/user/UserDetails';
import {SHARED_IMPORTS} from "../../../../../shared/standalone-imports";

export interface UserStoresModalData {
  user: UserDetails;
  allStores: StoreSummary[];
}

/**
 * This component handles assigning stores to the user.
 */
@Component({
  selector: 'app-user-stores-modal',
  templateUrl: './user-stores-modal.component.html',
  styleUrls: ['./user-stores-modal.component.less'],
  imports: [SHARED_IMPORTS]
})
export class UserStoresModalComponent {
  private userService = inject(UserService);
  private toastr = inject(ToastrService);
  private destroyRef = inject(DestroyRef);
  dataService = inject(DataService);

  /** A modal of user stores form. */
  modalComponent = viewChild.required<ModalComponent>('modalComponent');
  /** A user, which is being edited. */
  user = signal<UserDetails>(undefined!);
  /** A list of all stores. */
  allStores = signal<StoreSummary[]>([]);

  /** A form values of selected store identifiers. */
  selectedStoreIds = linkedSignal(() => this.user().stores);
  /** A list of possible checkbox items to be selected. */
  storeIdsOptions = computed(() => this.allStores().map(store => new CheckboxItem(store.slug, store.name)));
  /** `True` if a request sending to backend is in progress. */
  loading = signal(false);

  static open(modalService: NgbModal, data: UserStoresModalData): NgbModalRef {
    const modalRef = modalService.open(UserStoresModalComponent);
    const component = modalRef.componentInstance as UserStoresModalComponent;
    component.user.set(data.user);
    component.allStores.set(data.allStores);
    return modalRef;
  }

  /**
   * Updates selected stores.
   * @param storeIds a list of store identifiers to be assigned to a user.
   */
  onStoreIdsChange(storeIds: string[]): void {
    this.selectedStoreIds.set(storeIds);
  }

  /**
   * Sends request to backend to assign stores to a user.
   */
  onSubmit(): void {
    this.loading.set(true);
    this.userService.assignUserStores(this.user().email, this.selectedStoreIds())
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(
                    {
                      next: () => {
                        this.modalComponent().activeModal.close();
                        this.loading.set(false);
                        this.toastr.success('User stores has been successfully created');
                      },
                      error: (error) => {
                        this.loading.set(false);
                        this.toastr.error(error.error.message);
                      }
                    }
            );
  }
}